import type { KeyboardEvent, MouseEvent } from 'react'

export type ActivateEvent<E extends Element = HTMLElement> = MouseEvent<E> | KeyboardEvent<E>

/**
 * Makes a clickable non-button element (a card, a row) reachable and operable
 * from the keyboard: button role, tab stop, Enter/Space activation. Spread onto
 * the element; returns nothing when there's no handler.
 *
 * Keys only activate when the element itself has focus — a nested button, or a
 * menu item portaled out but still bubbling through the React tree, handles its
 * own keys.
 */
export function clickableProps<E extends Element = HTMLElement>(onActivate?: (e: ActivateEvent<E>) => void) {
  if (!onActivate) return {}
  return {
    role: 'button' as const,
    tabIndex: 0,
    onClick: onActivate,
    onKeyDown: (e: KeyboardEvent<E>) => {
      if (e.target !== e.currentTarget) return
      if (e.key !== 'Enter' && e.key !== ' ') return
      // Space would otherwise scroll the page.
      e.preventDefault()
      onActivate(e)
    },
  }
}
